import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  Stack,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
} from '@mui/material';
import {
  AddRounded,
  ArchiveRounded,
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  DeleteOutlineRounded,
  PlaylistAddRounded,
  UnarchiveRounded,
} from '@mui/icons-material';
import type { SubjectAreaCode } from '@dosfilos/domain';
import { useConfirm } from '../../../components/ConfirmProvider';
import type {
  CurriculumTermPlanDto,
  ProgramCurriculumVersionDto,
} from '../../../lib/api/academic.api';
import { terminoParaGuardar } from '../../../lib/curriculum-term';
import { canMove, moveCourse } from '../../../lib/curriculum-order';

export interface CourseOption {
  id: string;
  name: string;
  code: string;
  /** Catalog credits. Every course has them; they are what the plan is summed in. */
  credits?: number;
  /** From the course's CCD. Absent = no CCD yet, which is itself the CAP gap. */
  area?: SubjectAreaCode;
  tcu?: number;
  /** Inactive courses are loaded so the malla can name them, but never offered
   *  in the picker: a plan cannot require a course nobody can section. */
  inactive?: boolean;
}

// Workaround for MUI's exactOptionalPropertyTypes incompatibility in Autocomplete renderInput.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const autocompleteField = (params: any) => params as React.ComponentProps<typeof TextField>;

// Merge a patch into a term, dropping optional keys that end up empty/undefined
// (required under exactOptionalPropertyTypes).
/** Qué año y semestre proponer para un término nuevo, siguiendo al último.
 *
 *  Si el último ocupaba el primer semestre, el nuevo es el segundo del MISMO
 *  año; si ocupaba el segundo o el año entero, pasa al año siguiente. Es la
 *  secuencia normal de una malla, y deja el caso raro para quien lo corrija a
 *  mano. Sin ninguna propuesta, el campo queda vacío y la escalera se cae al
 *  dibujo apilado sin que nadie entienda por qué. */
function siguienteAnio(terms: CurriculumTermPlanDto[]): { year?: number; semester?: number } {
  const ultimo = [...terms].sort((a, b) => a.termOffset - b.termOffset).at(-1);
  if (!ultimo?.year) return {};
  return ultimo.semester === 1
    ? { year: ultimo.year, semester: 2 }
    : { year: ultimo.year + 1, ...(ultimo.semester === 2 ? { semester: 1 } : {}) };
}

function buildTerm(
  base: CurriculumTermPlanDto,
  patch: { [K in keyof CurriculumTermPlanDto]?: CurriculumTermPlanDto[K] | undefined },
): CurriculumTermPlanDto {
  // La MISMA regla que usa el guardado — `lib/curriculum-term.ts`. Escrita dos
  // veces, el editor y la pantalla conservaban campos distintos, y así se
  // perdieron `year` y `semester` en producción.
  //
  // LOS DOS CAMPOS OBLIGATORIOS SE RESUELVEN ACÁ, y no con un `as`: el patch
  // los declara opcionales, así que un `{ termOffset: undefined }` pasaría el
  // compilador y dejaría un término sin su posición. Se cae a `base`, que es lo
  // que hacía esta función antes de unificarse.
  const merged = { ...base, ...patch };
  return terminoParaGuardar({
    ...merged,
    termOffset: merged.termOffset ?? base.termOffset,
    recommendedCourseIds: merged.recommendedCourseIds ?? base.recommendedCourseIds,
  });
}

interface CurriculumMapEditorProps {
  value: ProgramCurriculumVersionDto[];
  onChange: (next: ProgramCurriculumVersionDto[]) => void;
  currentCatalogYear: string | null;
  onCurrentCatalogYearChange: (next: string | null) => void;
  courses: CourseOption[];
}

/** Credits + TCU of a set of courses, plus how many have no CCD yet. */
function sumOf(ids: string[], byId: Map<string, CourseOption>) {
  let credits = 0;
  let tcu = 0;
  let withoutCcd = 0;
  for (const id of ids) {
    const c = byId.get(id);
    if (!c) continue;
    credits += c.credits ?? 0;
    if (c.tcu === undefined) withoutCcd++;
    else tcu += c.tcu;
  }
  return { credits, tcu, withoutCcd };
}

export function CurriculumMapEditor({
  value,
  onChange,
  currentCatalogYear,
  onCurrentCatalogYearChange,
  courses,
}: CurriculumMapEditorProps) {
  const { t } = useTranslation('sis');
  // Area names live in the `cap` namespace — same source the malla legend uses,
  // so the picker and the drawing never disagree on what an area is called.
  const { t: tCap } = useTranslation('cap');
  const confirm = useConfirm();

  const byId = useMemo(() => new Map(courses.map((c) => [c.id, c])), [courses]);
  const courseLabel = (c: CourseOption) => (c.code ? `${c.code} — ${c.name}` : c.name);

  // Versions that share a catalogYear with an earlier one — flagged inline.
  const duplicateYears = new Set<string>();
  const seen = new Set<string>();
  value.forEach((v) => {
    const y = v.catalogYear.trim();
    if (!y) return;
    if (seen.has(y)) duplicateYears.add(y);
    seen.add(y);
  });

  const replaceVersion = (idx: number, next: ProgramCurriculumVersionDto) => {
    onChange(value.map((v, i) => (i === idx ? next : v)));
  };

  const addVersion = () => {
    onChange([...value, { catalogYear: '', terms: [] }]);
  };

  const removeVersion = async (idx: number) => {
    const removed = value[idx];
    if (!removed) return;
    // A catalog version is what a cohort of students is pinned to, so it is
    // never a throwaway click.
    const ok = await confirm({
      message: t('program.curriculum.confirmDeleteVersion', {
        year: removed.catalogYear.trim() || '—',
        count: removed.terms.length,
      }),
      confirmLabel: t('program.curriculum.confirmDeleteVersionAction'),
      confirmColor: 'error',
    });
    if (!ok) return;
    onChange(value.filter((_, i) => i !== idx));
    if (removed.catalogYear.trim() === (currentCatalogYear ?? '').trim()) {
      onCurrentCatalogYearChange(null);
    }
  };

  const addTerm = (vIdx: number) => {
    const version = value[vIdx];
    if (!version) return;
    const nextOffset =
      version.terms.length === 0 ? 0 : Math.max(...version.terms.map((tm) => tm.termOffset)) + 1;
    replaceVersion(vIdx, {
      ...version,
      // EL AÑO SE PROPONE, no se deja en blanco: un término sin año devuelve la
      // escalera entera al dibujo apilado, y quien agrega un término no tiene
      // por qué saberlo. Se sigue al último: mismo año si iba por el primer
      // semestre, el siguiente si no.
      terms: [
        ...version.terms,
        { termOffset: nextOffset, recommendedCourseIds: [], ...siguienteAnio(version.terms) },
      ],
    });
  };

  const replaceTerm = (vIdx: number, tIdx: number, next: CurriculumTermPlanDto) => {
    const version = value[vIdx];
    if (!version) return;
    replaceVersion(vIdx, {
      ...version,
      terms: version.terms.map((tm, i) => (i === tIdx ? next : tm)),
    });
  };

  const removeTerm = async (vIdx: number, tIdx: number) => {
    const version = value[vIdx];
    const term = version?.terms[tIdx];
    if (!version || !term) return;
    if (term.recommendedCourseIds.length > 0) {
      const ok = await confirm({
        message: t('program.curriculum.confirmDeleteTerm', {
          count: term.recommendedCourseIds.length,
        }),
        confirmColor: 'error',
      });
      if (!ok) return;
    }
    replaceVersion(vIdx, {
      ...version,
      terms: version.terms.filter((_, i) => i !== tIdx),
    });
  };

  /** Swap a term with its neighbour, exchanging their offsets so the stored
   *  order matches what the screen shows. */
  const moveTerm = (vIdx: number, tIdx: number, direction: -1 | 1) => {
    const version = value[vIdx];
    if (!version) return;
    const ordered = [...version.terms].sort((a, b) => a.termOffset - b.termOffset);
    const pos = ordered.findIndex((tm) => tm === version.terms[tIdx]);
    const target = ordered[pos + direction];
    const current = ordered[pos];
    if (!target || !current) return;
    const currentOffset = current.termOffset;
    replaceVersion(vIdx, {
      ...version,
      terms: version.terms.map((tm) => {
        if (tm === current) return { ...tm, termOffset: target.termOffset };
        if (tm === target) return { ...tm, termOffset: currentOffset };
        return tm;
      }),
    });
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t('program.curriculum.helper')}
      </Typography>

      {value.map((version, vIdx) => {
        const year = version.catalogYear.trim();
        const isDiscontinued = version.status === 'discontinued';
        // A retired plan can never be the live catalog — new students would be
        // pinned to something the institution stopped offering.
        const isCurrent = !isDiscontinued && !!year && year === (currentCatalogYear ?? '').trim();
        const isDuplicate = !!year && duplicateYears.has(year);
        const sortedTerms = version.terms
          .map((tm, tIdx) => ({ tm, tIdx }))
          .sort((a, b) => a.tm.termOffset - b.tm.termOffset);
        const allIds = version.terms.flatMap((tm) => tm.recommendedCourseIds);
        const planTotals = sumOf(allIds, byId);

        return (
          <Paper
            key={vIdx}
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, borderColor: isCurrent ? 'primary.main' : 'divider' }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <TextField
                label={t('program.curriculum.catalogYear')}
                value={version.catalogYear}
                onChange={(e) => replaceVersion(vIdx, { ...version, catalogYear: e.target.value })}
                error={isDuplicate}
                helperText={isDuplicate ? t('program.curriculum.duplicateYear') : undefined}
                size="small"
                sx={{ maxWidth: 200 }}
              />
              <FormControlLabel
                control={
                  <Radio
                    checked={isCurrent}
                    disabled={!year || isDiscontinued}
                    onChange={() => onCurrentCatalogYearChange(year || null)}
                  />
                }
                label={t('program.curriculum.current')}
              />
              {isDiscontinued && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('program.curriculum.discontinued')}
                  sx={{ alignSelf: 'center' }}
                />
              )}
              <Box sx={{ flexGrow: 1 }} />
              {/* Retiring is the ordinary operation; deleting is for a version
                  authored by mistake, and the server refuses it once anybody is
                  pinned to the plan. */}
              <Tooltip
                title={
                  isDiscontinued
                    ? t('program.curriculum.reactivateVersion')
                    : t('program.curriculum.discontinueVersion')
                }
              >
                <IconButton
                  onClick={() =>
                    replaceVersion(vIdx, {
                      ...version,
                      ...(isDiscontinued ? { status: 'active' } : { status: 'discontinued' }),
                    })
                  }
                  size="small"
                >
                  {isDiscontinued ? (
                    <UnarchiveRounded fontSize="small" />
                  ) : (
                    <ArchiveRounded fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title={t('program.curriculum.deleteVersion')}>
                <IconButton onClick={() => void removeVersion(vIdx)} size="small" color="error">
                  <DeleteOutlineRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {sortedTerms.map(({ tm, tIdx }, position) => {
                const totals = sumOf(tm.recommendedCourseIds, byId);
                // A course already placed in ANOTHER term of this same version.
                // Repeating it would double-count credits and draw it twice in
                // the malla, so the option says where it already is.
                const placedElsewhere = new Map<string, number>();
                sortedTerms.forEach(({ tm: other }, otherPos) => {
                  if (other === tm) return;
                  other.recommendedCourseIds.forEach((id) => placedElsewhere.set(id, otherPos + 1));
                });

                return (
                  <Paper
                    key={tIdx}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover' }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Chip
                        size="small"
                        label={t('program.curriculum.termNumber', { n: position + 1 })}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {t('program.curriculum.termTotals', {
                          count: tm.recommendedCourseIds.length,
                          credits: totals.credits,
                        })}
                        {totals.withoutCcd > 0
                          ? ` · ${t('program.curriculum.tcuPartial', {
                              tcu: totals.tcu,
                              n: totals.withoutCcd,
                            })}`
                          : ` · ${t('program.curriculum.tcu', { tcu: totals.tcu })}`}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Tooltip title={t('program.curriculum.moveUp')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={position === 0}
                            onClick={() => moveTerm(vIdx, tIdx, -1)}
                          >
                            <ArrowUpwardRounded fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('program.curriculum.moveDown')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={position === sortedTerms.length - 1}
                            onClick={() => moveTerm(vIdx, tIdx, 1)}
                          >
                            <ArrowDownwardRounded fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('program.curriculum.deleteTerm')}>
                        <IconButton
                          onClick={() => void removeTerm(vIdx, tIdx)}
                          size="small"
                          color="error"
                        >
                          <DeleteOutlineRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {/* EL AÑO ES LO QUE COLOCA EL TÉRMINO EN LA ESCALERA. La
                          etiqueta de al lado es sólo un nombre: dos programas
                          que escriben «Año 2» distinto igual comparten columna
                          si declaran el mismo año. */}
                      <TextField
                        label={t('program.curriculum.termYear')}
                        helperText={t('program.curriculum.termYearHelp')}
                        type="number"
                        value={tm.year ?? ''}
                        onChange={(e) =>
                          replaceTerm(
                            vIdx,
                            tIdx,
                            buildTerm(tm, {
                              year: e.target.value === '' ? undefined : Number(e.target.value),
                            }),
                          )
                        }
                        size="small"
                        inputProps={{ min: 1, 'aria-label': t('program.curriculum.termYear') }}
                        sx={{ width: 110 }}
                      />
                      <TextField
                        label={t('program.curriculum.termSemester')}
                        helperText={t('program.curriculum.termSemesterHelp')}
                        select
                        value={tm.semester ?? ''}
                        onChange={(e) =>
                          replaceTerm(
                            vIdx,
                            tIdx,
                            buildTerm(tm, {
                              semester: e.target.value === '' ? undefined : Number(e.target.value),
                            }),
                          )
                        }
                        size="small"
                        sx={{ width: 160 }}
                      >
                        <MenuItem value="">{t('program.curriculum.wholeYear')}</MenuItem>
                        <MenuItem value={1}>{t('programLadder.semester', { n: 1 })}</MenuItem>
                        <MenuItem value={2}>{t('programLadder.semester', { n: 2 })}</MenuItem>
                      </TextField>
                      <TextField
                        label={t('program.curriculum.termLabel')}
                        placeholder={t('program.curriculum.termLabelPlaceholder', {
                          n: position + 1,
                        })}
                        value={tm.label ?? ''}
                        onChange={(e) =>
                          replaceTerm(vIdx, tIdx, buildTerm(tm, { label: e.target.value }))
                        }
                        size="small"
                        sx={{ minWidth: 180 }}
                      />
                      <TextField
                        label={t('program.curriculum.electiveSlots')}
                        type="number"
                        value={tm.electiveSlots ?? ''}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          replaceTerm(
                            vIdx,
                            tIdx,
                            buildTerm(tm, {
                              electiveSlots:
                                e.target.value === '' || !Number.isFinite(n) ? undefined : n,
                            }),
                          );
                        }}
                        size="small"
                        inputProps={{ min: 0 }}
                        sx={{ width: 190 }}
                        helperText={t('program.curriculum.electiveSlotsHelper')}
                      />
                      <Autocomplete
                        multiple
                        sx={{ flexGrow: 1, minWidth: 260 }}
                        options={courses}
                        getOptionLabel={courseLabel}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        getOptionDisabled={(opt) => placedElsewhere.has(opt.id)}
                        renderOption={(props, opt) => {
                          const inTerm = placedElsewhere.get(opt.id);
                          return (
                            <Box component="li" {...props} key={opt.id}>
                              <Stack sx={{ width: '100%' }}>
                                <Typography variant="body2">{courseLabel(opt)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t('program.curriculum.courseCredits', {
                                    count: opt.credits ?? 0,
                                  })}
                                  {opt.area
                                    ? ` · ${tCap(`subjectAreas.${opt.area}`, {
                                        defaultValue: opt.area,
                                      })}`
                                    : ` · ${t('program.curriculum.noCcd')}`}
                                  {inTerm
                                    ? ` · ${t('program.curriculum.alreadyInTerm', { n: inTerm })}`
                                    : ''}
                                </Typography>
                              </Stack>
                            </Box>
                          );
                        }}
                        value={tm.recommendedCourseIds
                          .map((id) => byId.get(id))
                          .filter((c): c is CourseOption => !!c)}
                        onChange={(_, selected) =>
                          replaceTerm(vIdx, tIdx, {
                            ...tm,
                            recommendedCourseIds: selected.map((c) => c.id),
                          })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...autocompleteField(params)}
                            label={t('program.curriculum.courses')}
                            size="small"
                          />
                        )}
                        // Las materias se ordenan en la lista de abajo, no como
                        // fichas sueltas: el orden del período es contenido —lo
                        // leen la vista malla, la escalera y los anexos que
                        // recibe TMAI— y arrastrar fichas no dice cuál va primero.
                        renderTags={() => null}
                      />
                    </Stack>

                    {tm.recommendedCourseIds.length > 0 && (
                      <Stack sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('program.curriculum.orderHelp')}
                        </Typography>
                        {tm.recommendedCourseIds.map((id, cIdx) => {
                          const course = byId.get(id);
                          const reorder = (delta: number) =>
                            replaceTerm(vIdx, tIdx, {
                              ...tm,
                              recommendedCourseIds: moveCourse(
                                tm.recommendedCourseIds,
                                cIdx,
                                delta,
                              ),
                            });
                          return (
                            <Stack
                              key={id}
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{
                                py: 0.5,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:last-of-type': { borderBottom: 0 },
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  width: 22,
                                  textAlign: 'right',
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {cIdx + 1}
                              </Typography>
                              <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }}>
                                {course ? courseLabel(course) : id}
                                {course?.credits !== undefined && (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {` · ${t('program.curriculum.courseCredits', { count: course.credits })}`}
                                  </Typography>
                                )}
                              </Typography>
                              <Tooltip title={t('program.curriculum.moveCourseUp')}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!canMove(tm.recommendedCourseIds, cIdx, -1)}
                                    onClick={() => reorder(-1)}
                                    aria-label={t('program.curriculum.moveCourseUp')}
                                  >
                                    <ArrowUpwardRounded fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={t('program.curriculum.moveCourseDown')}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!canMove(tm.recommendedCourseIds, cIdx, 1)}
                                    onClick={() => reorder(1)}
                                    aria-label={t('program.curriculum.moveCourseDown')}
                                  >
                                    <ArrowDownwardRounded fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={t('program.curriculum.removeCourse')}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    replaceTerm(vIdx, tIdx, {
                                      ...tm,
                                      recommendedCourseIds: tm.recommendedCourseIds.filter(
                                        (x) => x !== id,
                                      ),
                                    })
                                  }
                                  aria-label={t('program.curriculum.removeCourse')}
                                >
                                  <DeleteOutlineRounded fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </Paper>
                );
              })}
              <Stack direction="row" alignItems="center" spacing={2}>
                <Button
                  size="small"
                  startIcon={<PlaylistAddRounded />}
                  onClick={() => addTerm(vIdx)}
                >
                  {t('program.curriculum.addTerm')}
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {/* The number a registrar checks first: does the plan add up to
                    what the degree requires? */}
                <Typography variant="body2" fontWeight={600}>
                  {t('program.curriculum.planTotals', {
                    count: allIds.length,
                    credits: planTotals.credits,
                  })}
                  {planTotals.withoutCcd > 0
                    ? ` · ${t('program.curriculum.tcuPartial', {
                        tcu: planTotals.tcu,
                        n: planTotals.withoutCcd,
                      })}`
                    : ` · ${t('program.curriculum.tcu', { tcu: planTotals.tcu })}`}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        );
      })}

      <Box>
        <Button variant="outlined" startIcon={<AddRounded />} onClick={addVersion}>
          {t('program.curriculum.addVersion')}
        </Button>
      </Box>
    </Stack>
  );
}
